import { Ruler, Users2, User, HeartPulse, Share2, Sparkles, Shield } from "lucide-react";

// Enum options — kept in one file so future sections can share.
export const BODY_TYPES = ["Toned", "Average", "Large", "Muscular", "Slim", "Stocky", "Bear"];
export const POSITIONS = ["Top", "Vers Top", "Vers", "Vers Bottom", "Bottom", "Side"];
export const ETHNICITIES = ["Asian", "Black", "Latino", "Middle Eastern", "Mixed", "Native American", "South Asian", "White", "Other"];
export const RELATIONSHIP_STATUSES = ["Single", "Dating", "Committed", "Married", "Open Relationship", "Partnered"];
export const GENDERS = ["Man", "Woman", "Non-binary", "Trans Man", "Trans Woman", "Cis Man", "Cis Woman", "Genderqueer", "Genderfluid"];
export const PRONOUNS = ["he/him", "she/her", "they/them", "he/they", "she/they", "any"];
export const TRIBES = ["Bear", "Clean-Cut", "Daddy", "Discreet", "Geek", "Jock", "Leather", "Otter", "Poz", "Rugged", "Sober", "Trans", "Twink"];
export const LOOKING_FOR_OPTIONS = ["Chat", "Dates", "Friends", "Networking", "Relationship", "Right Now", "Hookups"];
export const MEET_AT_OPTIONS = ["My Place", "Your Place", "Bar", "Coffee Shop", "Restaurant", "Public Space"];
export const HIV_STATUSES = ["Do Not Show", "Negative", "Negative, on PrEP", "Positive", "Positive, Undetectable"];

/** Section shell — icon + title + slot for rows. */
export function Section({ icon: Icon, title, children }) {
  return (
    <section className="mt-6" data-testid={`profile-section-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2 px-1 pb-3 text-xs uppercase tracking-[0.25em] text-zinc-400">
        {Icon && <Icon size={14} className="text-zinc-500" />}
        {title}
      </div>
      <div className="bg-zinc-950/50 border border-white/5 rounded-md overflow-hidden divide-y divide-white/5">
        {children}
      </div>
    </section>
  );
}

/** A single row (label left, value/control right). Consistent across sections. */
export function Row({ label, hint, right, testId }) {
  return (
    <div className="flex items-center justify-between px-4 py-3.5 gap-3" data-testid={testId}>
      <div className="min-w-0">
        <div className="text-[15px] text-white">{label}</div>
        {hint && <div className="text-[11px] text-zinc-500 mt-0.5 leading-tight">{hint}</div>}
      </div>
      <div className="shrink-0 flex items-center gap-2 max-w-[55%] text-right">{right}</div>
    </div>
  );
}

/** iOS-style pill toggle. Yellow when on to match the mobile mock. */
export function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-7 rounded-full transition-colors ${
        checked ? "bg-yellow-400" : "bg-white/15"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

/** Compact inline text input for row values. */
export function InlineInput({ value, onChange, placeholder, type = "text", testId, maxLength, className = "" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      data-testid={testId}
      className={`bg-transparent text-right text-[15px] text-white placeholder-zinc-600 focus:outline-none w-full ${className}`}
    />
  );
}

/** Native <select> styled to match the row. */
export function InlineSelect({ value, onChange, options, testId, placeholder = "Select" }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="bg-transparent text-right text-[15px] text-white focus:outline-none appearance-none pr-1"
    >
      <option value="" className="bg-zinc-900">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-zinc-900">{o}</option>
      ))}
    </select>
  );
}

/** Multi-select chip picker — used for tribes, looking-for, meet-at. */
export function ChipMultiSelect({ options, value, onChange, testId, max = 12 }) {
  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else if (value.length < max) onChange([...value, opt]);
  };
  return (
    <div className="px-4 pt-1 pb-4 flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((opt) => {
        const on = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              on
                ? "bg-yellow-400 border-yellow-300 text-black"
                : "bg-black/40 border-white/10 text-zinc-300 hover:border-white/25 hover:text-white"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export const SectionIcons = { Ruler, Users2, User, HeartPulse, Share2, Sparkles, Shield };
